// src/pages/TextesReglementaires/ConditionsUtilisationPage.jsx

import React, { useEffect, useMemo, useState } from "react";

import useScrollToTop from "../../hooks/useScollToTop";
import { getSettings } from "../../services/settingsServices";

import LegalPageLayout from "./components/LegalPageLayout";
import {
  formatLegalDate,
  getMairieIdentity,
  getSettingsRecord,
} from "./utils/legalPagesUtils";

export default function ConditionsUtilisationPage() {
  useScrollToTop();

  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);

      try {
        const data = await getSettings();

        if (mounted) {
          setSettings(getSettingsRecord(data));
        }
      } catch (error) {
        console.error("Erreur chargement settings conditions:", error);

        if (mounted) {
          setSettings({});
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const identity = useMemo(() => getMairieIdentity(settings), [settings]);
  const updatedAt = formatLegalDate(settings.updated_at);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1240px] px-4 pb-16 pt-32 sm:px-6">
        <div className="animate-pulse space-y-5">
          <div className="h-8 w-72 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-5 w-full max-w-2xl rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="h-96 rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <LegalPageLayout
      title="Conditions d’utilisation"
      eyebrow="Textes réglementaires"
      description="Conditions applicables à la consultation et à l’utilisation du site internet officiel de la mairie."
      identity={identity}
      updatedAt={updatedAt}
    >
      <h2>1. Objet du site</h2>
      <p>
        Les présentes conditions d’utilisation ont pour objet de définir les
        modalités d’accès et d’utilisation du site internet{" "}
        <strong>{identity.siteUrl}</strong>, édité par{" "}
        <strong>{identity.name}</strong>.
      </p>

      <p>
        Le site a pour vocation de fournir aux administrés, visiteurs et
        partenaires des informations pratiques sur la commune, ses services
        municipaux, ses actualités, ses publications, ses démarches, ses projets
        et la vie locale.
      </p>

      <h2>2. Acceptation des conditions</h2>
      <p>
        En consultant le site, l’utilisateur reconnaît avoir pris connaissance
        des présentes conditions d’utilisation et s’engage à les respecter.
      </p>

      <p>
        Si l’utilisateur n’accepte pas ces conditions, il lui appartient de ne
        pas utiliser le site.
      </p>

      <h2>3. Accès au site</h2>
      <p>
        La mairie s’efforce de maintenir le site accessible au public. Toutefois,
        l’accès peut être interrompu temporairement pour des raisons de
        maintenance, de mise à jour, de sécurité ou de force majeure.
      </p>

      <p>
        La mairie ne peut garantir une disponibilité permanente du site ni
        l’absence totale d’erreurs, d’interruptions ou d’anomalies techniques.
      </p>

      <h2>4. Informations publiées</h2>
      <p>
        Les informations diffusées sur le site sont fournies à titre indicatif
        et ont vocation à faciliter l’accès aux informations municipales.
      </p>

      <p>
        La mairie met tout en œuvre pour assurer l’exactitude et la mise à jour
        des contenus publiés. Toutefois, les informations peuvent évoluer. En cas
        de doute, l’usager est invité à contacter directement les services
        municipaux.
      </p>

      <h2>5. Utilisation du site</h2>
      <p>L’utilisateur s’engage à utiliser le site de manière loyale et conforme à sa destination.</p>

      <p>Il est notamment interdit :</p>

      <ul>
        <li>d’utiliser le site à des fins frauduleuses ou illicites ;</li>
        <li>
          de tenter de perturber le fonctionnement normal du site ou de ses
          serveurs ;
        </li>
        <li>
          de porter atteinte à la sécurité du site ou aux données qui y sont
          associées ;
        </li>
        <li>
          de réutiliser les contenus du site d’une manière susceptible de nuire à
          l’image de la commune ou d’induire les usagers en erreur.
        </li>
      </ul>

      <h2>6. Propriété intellectuelle</h2>
      <p>
        Les contenus présents sur le site, notamment les textes, images,
        photographies, graphismes, logos, documents et éléments de mise en page,
        sont protégés par le droit de la propriété intellectuelle, sauf mention
        contraire.
      </p>

      <p>
        Toute reproduction, représentation, modification, diffusion ou
        réutilisation des contenus doit respecter les droits de la mairie, de ses
        partenaires et des éventuels titulaires de droits.
      </p>

      <h2>7. Liens vers des sites externes</h2>
      <p>
        Le site peut contenir des liens vers des sites internet tiers. Ces liens
        sont proposés pour faciliter l’information des usagers.
      </p>

      <p>
        La mairie n’exerce aucun contrôle permanent sur ces sites externes et ne
        peut être tenue responsable de leurs contenus, de leurs pratiques ou de
        leur disponibilité.
      </p>

      <h2>8. Données personnelles</h2>
      <p>
        L’utilisation du site peut entraîner la collecte de certaines données
        personnelles, notamment lorsqu’un usager utilise un formulaire de
        contact, une demande en ligne ou un service interactif.
      </p>

      <p>
        Les modalités de traitement des données personnelles sont précisées dans
        la politique de confidentialité et la page relative au RGPD du site.
      </p>

      <h2>9. Cookies et traceurs</h2>
      <p>
        Le site peut utiliser des cookies ou traceurs nécessaires à son bon
        fonctionnement, ainsi que, le cas échéant, des outils de mesure
        d’audience ou services tiers.
      </p>

      <p>
        Lorsque le consentement de l’utilisateur est requis, celui-ci doit pouvoir
        accepter, refuser ou modifier ses choix selon les modalités indiquées sur
        le site.
      </p>

      <h2>10. Responsabilité</h2>
      <p>
        La mairie ne saurait être tenue responsable des dommages directs ou
        indirects résultant de l’accès au site, de son utilisation, d’une
        interruption de service, d’une erreur technique ou de l’utilisation
        d’informations disponibles sur le site.
      </p>

      <p>
        L’utilisateur reste responsable de son équipement, de sa connexion
        internet et de l’usage qu’il fait des informations consultées.
      </p>

      <h2>11. Modification des conditions</h2>
      <p>
        La mairie peut modifier les présentes conditions d’utilisation à tout
        moment afin de tenir compte des évolutions du site, des services proposés
        ou du cadre légal applicable.
      </p>

      <p>
        La version applicable est celle publiée sur le site au moment de la
        consultation.
      </p>

      <h2>12. Contact</h2>
      <p>
        Pour toute question relative à l’utilisation du site, l’utilisateur peut
        contacter <strong>{identity.name}</strong>
        {identity.email ? (
          <>
            {" "}
            à l’adresse suivante :{" "}
            <a href={`mailto:${identity.email}`}>{identity.email}</a>.
          </>
        ) : (
          "."
        )}
      </p>

      {identity.address && (
        <p>
          Adresse : <strong>{identity.address}</strong>
        </p>
      )}
    </LegalPageLayout>
  );
}